/**
 * Building blocks for the long legal pages — the privacy policy and the terms of
 * use (§15).
 *
 * Both pages are the published counterpart of a document in `docs/`, both run to
 * many sections, and both are read by someone looking for one specific paragraph.
 * Keeping their structure here means a reader gets the same contents list, the same
 * heading outline and the same table treatment on each, and it keeps the page files
 * as what they should be: the text, and nothing else.
 *
 * Accessibility decisions live here rather than in the pages, so a page cannot
 * accidentally ship an unlabelled table or a contents list that is not navigation.
 */

import { el } from "./dom.js";
import { pageHeader, section } from "./patterns.js";
import { SUPPORT_EMAIL, supportHref } from "../config/support.js";

/**
 * The width below which a policy table stops being a grid of columns and stacks
 * into one labelled field per line (see `policyTable`).
 *
 * `app.css` has to know the same threshold, to move a row's separating rule and
 * to drop the minimum width that keeps the columns legible above it. There is no
 * way to share one value between a media query and an attribute, so the two are
 * written out in both places and must be changed together.
 */
const TABLE_STACK_BREAKPOINT = "(max-width: 39.9375em)";

/**
 * One section of a legal document. `id` is its anchor, and the contents list is
 * built from the same objects, so a link and its target cannot drift apart.
 *
 * @typedef {{ id: string, title: string, content: (Node | string)[] }} LegalSection
 */

/**
 * A numbered clause. A string is a clause on its own; a node list is a clause with
 * emphasis or a link inside it; the object form carries sub-clauses.
 *
 * @typedef {string
 *   | (Node | string)[]
 *   | { text: string | (Node | string)[], sub: ClauseItem[] }} ClauseItem
 */

/**
 * Render a complete legal document: heading, contents and sections.
 *
 * In-page links are plain hash anchors. The router only intercepts `[data-link]`,
 * so the browser handles them itself and no route change occurs.
 *
 * @param {{ title: string, updated: string, slug: string, sections: LegalSection[] }} document
 * @returns {DocumentFragment}
 */
export function renderLegalDocument({ title, updated, slug, sections }) {
  const frag = document.createDocumentFragment();
  frag.append(pageHeader(title, `Last updated: ${updated}`));
  const contentsId = `${slug}-contents`;
  frag.append(
    el("nav", { class: "kin-toc", "aria-labelledby": contentsId }, [
      el("h2", { id: contentsId }, ["Contents"]),
      el(
        "ol",
        { class: "kin-plain-list" },
        sections.map((entry) => el("li", {}, [el("a", { href: `#${entry.id}` }, [entry.title])])),
      ),
    ]),
  );

  for (const entry of sections) {
    const node = section(entry.title, entry.content);
    node.id = entry.id;
    frag.append(node);
  }

  return frag;
}

/**
 * A paragraph of legal prose, held to a readable measure.
 *
 * @param {(Node | string)[]} children
 * @returns {HTMLElement}
 */
export function prose(children) {
  return el("p", { class: "kin-prose" }, children);
}

/**
 * A bulleted list. Items are plain strings, or node lists where a bullet contains
 * emphasis or a link.
 *
 * @param {(string | (Node | string)[])[]} items
 * @returns {HTMLElement}
 */
export function bullets(items) {
  return el(
    "ul",
    { class: "kin-plain-list kin-prose" },
    items.map((item) => el("li", {}, Array.isArray(item) ? item : [item])),
  );
}

/**
 * A numbered clause list, nestable for sub-clauses.
 *
 * Real ordered lists rather than numbers typed into the text: a screen reader then
 * announces the position and depth of a clause, and a sub-clause cannot end up
 * numbered as a clause in its own right — which is exactly the mistake that made
 * clauses 11 to 16 of the terms unreadable in the source document.
 *
 * @param {ClauseItem[]} items
 * @returns {HTMLElement}
 */
export function clauses(items) {
  return el("ol", { class: "kin-clause-list kin-prose" }, items.map(clause));
}

/**
 * @param {ClauseItem} item
 * @returns {HTMLElement}
 */
function clause(item) {
  if (typeof item === "string") {
    return el("li", {}, [item]);
  }
  if (Array.isArray(item)) {
    return el("li", {}, item);
  }
  return el("li", {}, [
    ...(typeof item.text === "string" ? [item.text] : item.text),
    clauses(item.sub),
  ]);
}

/**
 * A sub-heading inside a section. h3 keeps the outline contiguous beneath the
 * section's h2 (WCAG 2.2 AA 1.3.1).
 *
 * @param {string} text
 * @returns {HTMLElement}
 */
export function subheading(text) {
  return el("h3", {}, [text]);
}

/** @returns {HTMLElement} A link to the support address. */
export function supportLink() {
  return el("a", { href: supportHref() }, [SUPPORT_EMAIL]);
}

/**
 * A table of policy detail.
 *
 * These tables are three columns of prose — a purpose, the data it uses, and the
 * legal basis for it — and a phone cannot show three columns of prose. `table-saw`
 * stacks them below `TABLE_STACK_BREAKPOINT`, so each cell becomes its own line
 * prefixed with the column it belongs to, and the reader scrolls the page they
 * were already scrolling instead of dragging a table sideways to find the third
 * column. Above the breakpoint nothing changes, and without JavaScript the plain
 * table is what renders.
 *
 * The roles are the price of that. `table-saw` stacks by overriding `display` on
 * the rows and cells, and a browser drops the implicit table semantics when it
 * does — rows and cells stop being rows and cells in the accessibility tree, and
 * a screen-reader user loses the relationship between a value and its column
 * (WCAG 2.2 AA 1.3.1). Stating each role explicitly restores it. Above the
 * breakpoint every one of them merely repeats what the element already meant.
 *
 * The scroll container remains for the width between the breakpoint and the point
 * where the columns fit: a region that scrolls must be reachable by keyboard alone
 * (WCAG 2.2 AA 2.1.1), so it is focusable and labelled.
 *
 * @param {string} caption
 * @param {string[]} headers
 * @param {string[][]} rows
 * @returns {HTMLElement}
 */
export function policyTable(caption, headers, rows) {
  return el(
    "div",
    { class: "kin-table-scroll", role: "region", "aria-label": caption, tabindex: "0" },
    [
      // `zero-padding` only applies once stacked, where the cells' side padding
      // would indent the text away from the prose above and below it.
      el("table-saw", { breakpoint: TABLE_STACK_BREAKPOINT, "zero-padding": true }, [
        el("table", { class: "kin-table", role: "table" }, [
          el("caption", {}, [caption]),
          el("thead", { role: "rowgroup" }, [
            el(
              "tr",
              { role: "row" },
              headers.map((heading) => el("th", { scope: "col", role: "columnheader" }, [heading])),
            ),
          ]),
          el(
            "tbody",
            { role: "rowgroup" },
            rows.map((cells) =>
              el(
                "tr",
                { role: "row" },
                cells.map((cell, index) =>
                  index === 0
                    ? el("th", { scope: "row", role: "rowheader" }, [cell])
                    : el("td", { role: "cell" }, [cell]),
                ),
              ),
            ),
          ),
        ]),
      ]),
    ],
  );
}
